import React, { useState, useEffect } from 'react';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash, FaDownload, FaEye } from 'react-icons/fa';

const PdfPage = () => {
  const [pdfs, setPdfs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formValues, setFormValues] = useState({ title: '', image: '', pdf: '', active: true });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [uploading, setUploading] = useState(false);
  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10);

  useEffect(() => {
    fetchPdfs(currentPage, searchQuery);
  }, [currentPage, searchQuery]);

  const fetchPdfs = async (page = 1, search = '') => {
    try {
      const res = await axios.get(`/admin/api/pdf/getPdfAll?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`);
      const { pdfs: pdfArr = [], totalCount = 0 } = res.data;
      setPdfs(pdfArr);
      setTotalPages(Math.ceil((totalCount || pdfArr.length) / limit));
    } catch (err) {
      console.error('Failed to fetch PDFs:', err);
    }
  };

  // Handle file input change for image/pdf
  const handleFileChange = (name, file) => {
    if (!file) return;
    setFormValues((prev) => ({
      ...prev,
      [name]: file
    }));
  };

  const handleSave = async () => {
    if (uploading) {
      toastError('Please wait for file upload to finish.');
      return;
    }
    const validationErrors = validateForm(formValues, {
      title: { required: true },
      pdf: { required: true },
      image: { required: true },
    });
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    try {
      const formData = new FormData();
      formData.append('title', formValues.title);
      formData.append('active', formValues.active ? 'true' : 'false');
      // If image/pdf is a URL (already uploaded), skip; if it's a File, append as binary
      if (formValues.image instanceof File) {
        formData.append('image', formValues.image);
      } else if (formValues.image) {
        // If backend expects binary only, you may want to force re-upload
        // Otherwise, skip or handle as needed
      }
      if (formValues.pdf instanceof File) {
        formData.append('pdf', formValues.pdf);
      } else if (formValues.pdf) {
        // If backend expects binary only, you may want to force re-upload
        // Otherwise, skip or handle as needed
      }

      // If editing, send PUT; else POST
      if (editingId) {
        await axios.put(`/admin/api/pdf/${editingId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toastSuccess('PDF updated successfully!');
      } else {
        await axios.post('/admin/api/pdf', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toastSuccess('PDF added successfully!');
      }
      setEditingId(null);
      setCurrentPage(1);
      fetchPdfs(1, searchQuery);
      handleCancel();
    } catch (err) {
      console.error('Failed to save PDF:', err);
      toastError('Failed to save PDF');
    }
  };

  const handleEdit = (pdf) => {
    setFormValues({
      title: pdf.title || '',
      image: pdf.image || '',
      pdf: pdf.pdf || '',
      active: pdf.active !== undefined ? pdf.active : true,
    });
    setEditingId(pdf._id);
    setShowModal(true);
    // Optionally, clear file input fields in CommonModalForm if needed
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/admin/api/pdf/${id}`);
      toastSuccess('PDF deleted successfully!');
      setCurrentPage(1);
      fetchPdfs(1, searchQuery);
    } catch (err) {
      console.error('Failed to delete PDF:', err);
      toastError('Failed to delete PDF');
    }
  };

  // Toggle active status for a PDF
  const handleToggleActive = async (pdfItem) => {
    try {
      await axios.put(`/admin/api/pdf/${pdfItem._id}`, {
        ...pdfItem,
        active: !pdfItem.active,
      });
      toastSuccess(`PDF marked as ${pdfItem.active ? 'Inactive' : 'Active'}!`);
      fetchPdfs(currentPage, searchQuery);
    } catch (err) {
      toastError('Failed to update status');
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({ title: '', image: '', pdf: '', active: true });
    setEditingId(null);
    setErrors({});
  };

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">PDF Management</h3>
        <input
          type="text"
          className="form-control w-25"
          placeholder="Search PDF..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>
      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0">PDF List</h5>
          <button
            className="btn btn-sm btn-success"
            onClick={() => setShowModal(true)}
          >
            + Add New PDF
          </button>
        </div>
        <div className="table-responsive">
          <div className="card-body p-0">
            <table className="table table-bordered table-sm mb-0">
              <thead className="table-light">
                <tr>
                  <th className="py-1 px-2 w-5">Sr no.</th>
                  <th className="py-1 px-2">Title</th>
                  <th className="py-1 px-2">Image</th>
                  <th className="py-1 px-2">PDF</th>
                  <th className="py-1 px-2">Active</th>
                  <th className="py-1 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pdfs.length > 0 ? (
                  pdfs.map((pdfItem, idx) => (
                    <tr key={pdfItem._id}>
                      <td className="py-1 px-2">{(currentPage - 1) * limit + idx + 1}</td>
                      <td className="py-1 px-2">{pdfItem.title}</td>
                      <td className="py-1 px-2">
                        {pdfItem.image ? (
                          <img src={pdfItem.image} alt="pdf" style={{ width: 40, height: 40, objectFit: 'cover' }} />
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="py-1 px-2">
                        {pdfItem.pdf ? (
                          <>
                            <a href={pdfItem.pdf} target="_blank" rel="noopener noreferrer" className="me-2" title="View">
                              <FaEye />
                            </a>
                            <a href={pdfItem.pdf} download title="Download">
                              <FaDownload />
                            </a>
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="py-1 px-2">
                        <button
                          className={`btn btn-sm ${pdfItem.active ? 'btn-success' : 'btn-danger'}`}
                          style={{ minWidth: 70 }}
                          onClick={() => handleToggleActive(pdfItem)}
                          title={pdfItem.active ? 'Click to mark Inactive' : 'Click to mark Active'}
                        >
                          {pdfItem.active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="py-1 px-2">
                        <button
                          className="btn btn-sm me-1 py-0 px-2 fs-6 lh-sm shadow-none border-0 text-primary-custom"
                          title="Edit"
                          onClick={() => handleEdit(pdfItem)}
                        >
                          <FaEdit size={14} />
                        </button>
                        <button
                          className="btn btn-sm py-0 px-2 fs-6 lh-sm shadow-none border-0 text-danger"
                          onClick={() => handleDelete(pdfItem._id)}
                          title="Delete"
                        >
                          <FaTrash size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-2">No PDF found.</td>
                  </tr>
                )}
              </tbody>
            </table>
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-end align-items-center gap-2 p-2">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  &laquo;
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    className={`btn btn-sm page-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ))}
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  &raquo;
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <CommonModalForm
        visible={showModal}
        title={editingId ? 'Edit PDF' : 'Add PDF'}
        fields={[
          { name: 'title', label: 'Title', placeholder: 'Enter PDF Title' },
          {
            name: 'image',
            label: 'Image',
            type: 'file',
            accept: 'image/*',
            onFileChange: (file) => handleFileChange('image', file),
            // Optionally show current image
            extra: formValues.image
              ? <img src={formValues.image} alt="Current" style={{ width: 60, marginTop: 4 }} />
              : null
          },
          {
            name: 'pdf',
            label: 'PDF',
            type: 'file',
            accept: 'application/pdf',
            onFileChange: (file) => handleFileChange('pdf', file),
            // Optionally show current PDF link
            extra: formValues.pdf
              ? <a href={formValues.pdf} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginTop: 4 }}>Current PDF</a>
              : null
          },
          { name: 'active', label: 'Active', type: 'checkbox' }
        ]}
        values={formValues}
        errors={errors}
        onChange={setFormValues}
        onSubmit={handleSave}
        onCancel={handleCancel}
        submitLabel={uploading ? 'Uploading...' : (editingId ? 'Update' : 'Save')}
        requiredFields={['title', 'image', 'pdf']}
        disabled={uploading}
      />
    </div>
  );
};

export default PdfPage;
